import * as React from "react";
import { ActionsItem } from "@common/components/actions";
import { trDts } from "@modules/datasheets/utils/i18n";
import { getTestAttributes } from "@common/utils/tests";
import { DATASHEETS_MODULE_NAME } from "@modules/datasheets/constants/common";
import { useMessage } from "@common/hooks/message";
import { DatasheetEditorHeaderProps } from "./header.types";
import { DatasheetUpnListDto } from "@common/dto/datasheet/upn/list";
import { FormatFieldItem } from "@common/components/format-field-selector";

export const useController = (props: DatasheetEditorHeaderProps) => {
  const {
    disabledState,
    onPrint,
    onUndo,
    onRedo,
    onOrderTest,
    onGenerateLetter,
    onOrderTestResults,
    getUpns,
    onUpnChange,
  } = props;
  const { closeMessage, showErrorMessage, messageProps } = useMessage();

  const [upns, setUpns] = React.useState<Array<{ value: DatasheetUpnListDto["id"]; label: string }>>([]);

  const [datasheetFormatsManagementOpened, setDatasheetFormatsManagementOpened] = React.useState(false);

  React.useEffect(() => {
    getUpns()
      .then((list) => {
        setUpns(list.map((x) => ({ value: x.id, label: x.name })));
      })
      .catch((err) => {
        console.error(err);
        showErrorMessage(trDts("Failed to load UPNs"));
      });
  }, []);

  const handleUpnChange = React.useCallback(
    (value: DatasheetUpnListDto["id"]) => {
      onUpnChange(value);
    },
    [onUpnChange]
  );

  const actions: Array<ActionsItem> = [
    {
      iconSrc: "png/undo.png",
      label: trDts("Undo"),
      disabled: disabledState.undo,
      onClick: onUndo,
    },
    {
      iconSrc: "png/redo.png",
      label: trDts("Redo"),
      disabled: disabledState.redo,
      onClick: onRedo,
    },
    {
      iconSrc: "gif/print.gif",
      label: trDts("Print"),
      disabled: disabledState.print,
      onClick: onPrint,
    },
    {
      iconSrc: "png/order_test.png",
      label: trDts("Order Test"),
      disabled: disabledState.orderTest,
      onClick: onOrderTest,
    },
    {
      iconSrc: "png/results-icon.png",
      label: trDts("Test Results"),
      disabled: disabledState.testResults,
      onClick: onOrderTestResults,
    },
    {
      iconSrc: "png/letter.png",
      label: trDts("Generate Letter"),
      disabled: disabledState.generateLetter,
      onClick: onGenerateLetter,
    },
  ].map(({ label, ...restData }) => ({
    ...restData,
    id: getTestAttributes(DATASHEETS_MODULE_NAME.editor, "header-action", "btn", label.replace(" ", "-").toLowerCase())
      .id,
    label: label.toString(),
  }));

  const getMenuPortalStyles = React.useCallback((css: React.CSSProperties) => ({ ...css, zIndex: 9999 }), []);

  const handleDatasheetFormatsManagementOpen = React.useCallback(() => {
    setDatasheetFormatsManagementOpened(true);
  }, []);

  const handleDatasheetFormatsManagementClose = React.useCallback(() => {
    setDatasheetFormatsManagementOpened(false);
  }, []);

  const handleFormatAsTheDefaultDropdownClose = React.useCallback(() => {
    // TODO: Implement logic on BE integration
  }, []);

  const handleFormatAsTheDefaultDropdownChange = React.useCallback(
    (format: FormatFieldItem) => {
      handleFormatAsTheDefaultDropdownClose();
    },
    [handleFormatAsTheDefaultDropdownClose]
  );

  return {
    handleUpnChange,
    upns,
    actions,
    messageProps,
    closeMessage,
    showErrorMessage,
    datasheetFormatsManagementOpened,
    getMenuPortalStyles,
    handleDatasheetFormatsManagementOpen,
    handleDatasheetFormatsManagementClose,
    handleFormatAsTheDefaultDropdownClose,
    handleFormatAsTheDefaultDropdownChange,
  };
};
